import './PopulationPercentage.scss';

import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { debounce } from 'lodash';
import { FilterComponent } from './FilterComponent';

import { Flex, InputNumber, Slider, Space, Switch } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const min = 0;
const max = 100;

export const PopulationPercentageFilter: FilterComponent<[number | null, number | null, boolean] | null> = (props) => {
	const { format, id, onChange, value, label } = props;
	const [ displayRange, setDisplayRange ] = useState<[number, number]>([ value?.[ 0 ] ?? min, value?.[ 1 ] ?? max ]);
	const [ excludeNulls, setExcludeNulls ] = useState(value?.[ 2 ] ?? false);
	const showClearButton = value?.[ 0 ] || value?.[ 1 ];

	const excludeNullsText = value?.[ 2 ] ? ` ${COPY_SPECIFIC.EXCLUDING_EMPTY}` : '';
	const tagText = `${[ value?.[ 0 ], value?.[ 1 ] ].map((num) => `${num}%`).join(' - ')}${excludeNullsText}`;

	useEffect(() => {
		if (!value) {
			setDisplayRange([ min, max ]);
			setExcludeNulls(false);
		}
	}, [ value ]);

	const debouncedOnChange = useMemo(
		() => debounce((range: [number, number], hideNulls: boolean) => {
			const [ start, end ] = range;
			const removeFilter = start === min && end === max && !hideNulls;

			setDisplayRange(range);

			onChange({
				[ id ]: {
					format,
					value: removeFilter ? null : [ start, end, hideNulls ]
				}
			});
		}, 300, { leading: false }),
		[ format, id, onChange ]
	);

	const handleChangeRange = useCallback((range: [number, number]) => {
		setDisplayRange(range);
		debouncedOnChange(range, excludeNulls);
	}, [ debouncedOnChange, excludeNulls ]);

	const handleChangeStart = useCallback((start: number) => {
		const [ , end ] = displayRange;

		setDisplayRange([ start, end ]);
		debouncedOnChange([ start, Math.max(start, end) ], excludeNulls);
	}, [ debouncedOnChange, displayRange, excludeNulls ]);

	const handleChangeEnd = useCallback((end: number) => {
		const [ start ] = displayRange;

		setDisplayRange([ start, end ]);
		debouncedOnChange([ Math.min(start, end), end ], excludeNulls);
	}, [ debouncedOnChange, displayRange, excludeNulls ]);

	const handleToggleNulls = useCallback((toggle: boolean) => {
		setExcludeNulls(toggle);
		debouncedOnChange(displayRange, toggle);
	}, [ debouncedOnChange, displayRange ]);

	return (
		<FilterComponent
			{ ...props }
			className='population-percentage-filter'
			tagProps={ {
				label,
				value: showClearButton ? tagText : null
			} }>
			<Space
				direction='vertical'
				size={ 4 }>
				<Flex
					align='center'
					gap={ 16 }>
					<InputNumber
						max={ max }
						min={ min }
						onChange={ handleChangeStart }
						value={ displayRange[ 0 ] } />
					<Slider
						max={ max }
						min={ min }
						onChange={ handleChangeRange }
						range
						value={ [ displayRange[ 0 ], displayRange[ 1 ] ] } />
					<InputNumber
						max={ max }
						min={ min }
						onChange={ handleChangeEnd }
						value={ displayRange[ 1 ] } />
				</Flex>
				<Space size={ 8 }>
					<Switch
						checked={ excludeNulls ?? value?.[ 2 ] }
						onChange={ handleToggleNulls } />
					{ COPY_SPECIFIC.EXCLUDE_EMPTY_VALUES }
				</Space>
			</Space>
		</FilterComponent>
	);
};
